import { z } from "zod";

import type { McpToolStatusValue } from "./mcp-tool-attachability";

/**
 * What one server's advertised tools have done to the contract an organization
 * APPROVED — the three things an administrator has to answer or act on.
 *
 * ## It is a STANDING statement, not a diff between two syncs
 *
 * This is the property the whole file exists for, and the one a delta gets wrong.
 *
 * A report computed as *what changed in this sync* is correct exactly once. Sync 1
 * approves contract `A`. Sync 2 discovers `B`, reports the tool changed, and moves
 * it to `CHANGED`. Sync 3 discovers `B` again — nothing moved since sync 2 — so a
 * delta reports NOTHING, while the tool is still advertising a contract nobody
 * approved and is still refused at call time. The admin who runs sync 3 reads a
 * clean report over a broken tool.
 *
 * Every arm here is instead a statement about the CURRENT state, so it survives
 * any number of syncs that change nothing, and stops being reported only when an
 * administrator actually answers it. The same argument applies to `removed`:
 * `reconcileDiscoveredTools` deliberately does not re-stamp `removedAt` on a row
 * that is already REMOVED, so a delta would name a withdrawn tool once and never
 * again.
 *
 * ## Each arm is measured against the APPROVED hash, never the DISCOVERED one
 *
 * That is true by construction rather than by a comparison written here: the arms
 * are keyed on `McpToolStatus`, and `resolveRefreshedStatus` computes that status
 * by comparing the newly discovered hash against `approvedSchemaHash`. Writing a
 * hash comparison in this file would be a second copy of that decision table —
 * the exact thing `reconcile-discovered-tools.ts` refuses to let the write adapter
 * do — and a second copy is free to disagree with the first.
 *
 * It also could not be written correctly here: a REMOVED tool's discovered hash is
 * still equal to its approved one (it is the last thing the server advertised), so
 * a pure hash comparison scores it as no drift at all.
 *
 * ## Two statuses are deliberately in NO arm
 *
 * - **APPROVED** — the server advertises exactly what was approved. Nothing to do.
 * - **REJECTED** — an administrator already answered THIS contract. Reporting it
 *   every sync would put a permanent, unanswerable row in front of the admin, and
 *   a report that always has something in it is a report nobody reads. A rejected
 *   tool whose contract later CHANGES is not this case: `resolveRefreshedStatus`
 *   returns it to `PENDING_APPROVAL` and clears the rejection, so it reappears
 *   under `added` — a question the admin has genuinely never been asked.
 */
export const MCP_TOOL_DRIFT_ARMS = [
  /** Advertised now, with no approval on record. Awaiting a first answer. */
  "added",
  /** Approved once, and the server now advertises a different contract. */
  "changed",
  /** The server no longer advertises it. */
  "removed"
] as const;

export type McpToolDriftArm = (typeof MCP_TOOL_DRIFT_ARMS)[number];

/**
 * The arm each status falls in, or `null` for the two that are not drift.
 *
 * `Record<McpToolStatusValue, …>` is what makes this exhaustive at compile time:
 * a status added to `McpToolStatus` in `schema.prisma` regenerates
 * `MCP_TOOL_STATUS_VALUES`, and this object then fails to typecheck until somebody
 * decides which arm it belongs in. A `switch` with a `default`, or a lookup with a
 * `?? null` tail, would silently classify a new status as "not drift" — which is
 * the direction that hides a problem rather than inventing one.
 */
const DRIFT_ARM_BY_STATUS: Readonly<Record<McpToolStatusValue, McpToolDriftArm | null>> = {
  PENDING_APPROVAL: "added",
  CHANGED: "changed",
  REMOVED: "removed",
  APPROVED: null,
  REJECTED: null
};

/** The arm one tool's status falls in, or `null` when it is not drift. */
export function mcpToolDriftArm(status: McpToolStatusValue): McpToolDriftArm | null {
  return DRIFT_ARM_BY_STATUS[status];
}

/**
 * The three lists, by tool NAME.
 *
 * Names and nothing else. The name is the server's own identity for a tool — what
 * `@@unique([serverId, name])` compares and what `tools/call` is sent — so it is
 * the key a reader joins back to the `tools` array carried in the same response.
 * Repeating a status, a hash or a contract here would be a second rendering of
 * rows that are already in that response, and a second rendering is a second thing
 * that can disagree.
 */
export const McpToolDriftReportSchema = z.object({
  added: z.array(z.string()),
  changed: z.array(z.string()),
  removed: z.array(z.string())
});

export type McpToolDriftReport = z.infer<typeof McpToolDriftReportSchema>;

/** The columns the report is computed from. Nothing else is read. */
export interface McpToolDriftInput {
  readonly name: string;
  readonly status: McpToolStatusValue;
}

/**
 * The report for one server's tool rows.
 *
 * Sorted, because the input order is a database row order that nothing pins: an
 * unsorted report would re-order between two identical syncs, which makes the
 * response impossible to diff and a spec that asserts on it flaky in a way that
 * reads as a real change.
 */
export function summariseMcpToolDrift(tools: readonly McpToolDriftInput[]): McpToolDriftReport {
  const report: McpToolDriftReport = { added: [], changed: [], removed: [] };

  for (const tool of tools) {
    const arm = mcpToolDriftArm(tool.status);
    if (arm !== null) report[arm].push(tool.name);
  }

  for (const arm of MCP_TOOL_DRIFT_ARMS) report[arm].sort();
  return report;
}
